'use client';

/**
 * Footer news/blog rail — thumbnail + title + date cards in a
 * horizontally scrollable strip with prev/next arrows. Same rail
 * mechanic as {@code ArticleCarousel} (akademi-net theme), ported
 * here so any footer style can opt in via
 * {@code config.footer.resourceList}.
 *
 * <p>{@code source} selects the host adapter: {@code news} reads
 * {@link LearningSource.fetchArticles}, {@code blogs} reads
 * {@link BlogSource.listArticles}. Renders nothing when the selected
 * adapter isn't mounted or returns no items — a tenant that enables
 * this before wiring the corresponding host adapter doesn't get an
 * inert footer widget.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { useLearningSource, useBlogSource } from '../../portal';

const CARD_WIDTH = 400;
const CARD_GAP = 16;

interface ResourceItem {
  id: string;
  title: string;
  date?: string;
  cover?: string;
  href: string;
}

export interface FooterResourceListProps {
  title?: string;
  source?: 'news' | 'blogs';
  limit?: number;
  locale: string;
  tenantHref: (href: string | undefined) => string;
  /** Shown in the item thumbnail in place of a missing cover image. */
  fallbackLogo?: string;
}

export function FooterResourceList({
  title,
  source = 'news',
  limit = 6,
  locale,
  tenantHref,
  fallbackLogo,
}: FooterResourceListProps): ReactElement | null {
  const lms = useLearningSource();
  const blog = useBlogSource();
  const wrapRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<ResourceItem[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'empty'>('loading');
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [railWidth, setRailWidth] = useState<number | null>(null);

  useEffect(() => {
    const adapter = source === 'blogs' ? blog : lms;
    if (!adapter) {
      setState('empty');
      return;
    }
    let cancelled = false;
    setState('loading');
    const load = async () => {
      try {
        let rows: ResourceItem[];
        if (source === 'blogs' && blog) {
          const { items: articles } = await blog.listArticles({
            pagination: { page: 1, pageSize: limit },
          });
          rows = articles.map((a) => ({
            id: String(a.id),
            title: a.title,
            date: a.publishedAt,
            cover: a.cover,
            href: `/blog/${a.slug}`,
          }));
        } else if (lms) {
          const articles = await lms.fetchArticles({ limit, offset: 0 });
          rows = articles.map((a) => ({
            id: a.id,
            title: a.title,
            date: a.date,
            cover: a.cover,
            href: `/news/${a.id}`,
          }));
        } else {
          if (!cancelled) setState('empty');
          return;
        }
        if (cancelled) return;
        if (rows.length === 0) {
          setState('empty');
          return;
        }
        setItems(rows);
        setState('ready');
      } catch {
        if (!cancelled) setState('empty');
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [source, lms, blog, limit]);

  const syncEdges = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft >= max - 1);
  }, []);

  useEffect(() => {
    if (state !== 'ready') return;
    syncEdges();
    const el = railRef.current;
    if (!el) return;
    el.addEventListener('scroll', syncEdges, { passive: true });
    window.addEventListener('resize', syncEdges);
    return () => {
      el.removeEventListener('scroll', syncEdges);
      window.removeEventListener('resize', syncEdges);
    };
  }, [state, syncEdges]);

  // Clamp the rail to a whole number of cards. Without this, whenever
  // the available width isn't an exact multiple of (card + gap), the
  // last card either peeks in partially at the start or — worse —
  // gets pinned flush against the far edge at max scroll with no snap
  // point able to bring it flush-left (there's nowhere further to
  // scroll once the content runs out), which is what made the last
  // card look indented relative to the heading above it.
  useEffect(() => {
    if (state !== 'ready') return;
    const el = wrapRef.current;
    if (!el) return;
    const recompute = () => {
      const available = el.clientWidth;
      if (available <= 0) return;
      const step = CARD_WIDTH + CARD_GAP;
      const fullCards = Math.max(1, Math.floor((available + CARD_GAP) / step));
      setRailWidth(fullCards * step - CARD_GAP);
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [state]);

  if (state === 'empty') return null;

  const scroll = (dir: 'prev' | 'next') => {
    const rail = railRef.current;
    if (!rail) return;
    // One card per click — with `railWidth` clamped to a whole number
    // of cards, a full-step scroll always lands exactly on the next
    // snap point instead of drifting into a fractional position.
    const amount = CARD_WIDTH + CARD_GAP;
    rail.scrollBy({ left: dir === 'next' ? amount : -amount, behavior: 'smooth' });
  };

  return (
    <div style={{ paddingBlockEnd: 'var(--space-4, 16px)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBlockEnd: 12,
        }}
      >
        {title ? (
          <span style={{ fontSize: 'var(--font-size-sm, 14px)', fontWeight: 700 }}>{title}</span>
        ) : (
          <span />
        )}
        {state === 'ready' && items.length > 1 && (
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <RailArrowButton dir="prev" onClick={() => scroll('prev')} disabled={atStart} />
            <RailArrowButton dir="next" onClick={() => scroll('next')} disabled={atEnd} />
          </div>
        )}
      </div>

      <div ref={wrapRef}>
        <div
          ref={railRef}
          className="ak-footer-resource-rail"
          style={{
            display: 'grid',
            gridAutoFlow: 'column',
            gridAutoColumns: `${CARD_WIDTH}px`,
            gap: `${CARD_GAP}px`,
            // Clamped to a whole number of cards (see the effect above)
            // so the rail never ends mid-card — that partial trailing
            // column was both the "next card peeking in" at the start
            // and the "last card can't reach flush-left" glitch at the
            // end, since scroll-snap has nowhere valid to land past the
            // content's actual edge.
            width: railWidth ?? '100%',
            maxWidth: '100%',
            // `overflow: hidden` isn't a scroll container — it silently
            // breaks scroll-snap and makes the arrow buttons' scrollBy()
            // land on inconsistent offsets across browsers. Stay `auto`
            // (a real scroll container) and hide the scrollbar visually
            // instead — see the stylesheet below.
            overflowX: 'auto',
            scrollbarWidth: 'none',
            paddingBlockEnd: 4,
            scrollSnapType: 'inline mandatory',
          }}
        >
          {state === 'loading'
            ? Array.from({ length: Math.min(limit, 3) }).map((_, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }} aria-hidden>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      flexShrink: 0,
                      borderRadius: 'var(--radius-md, 10px)',
                      background: 'var(--color-border-subtle, rgba(0,0,0,0.08))',
                      animation: 'ak-footer-resource-pulse 1.4s ease-in-out infinite',
                    }}
                  />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div
                      style={{
                        height: 12,
                        borderRadius: 4,
                        background: 'var(--color-border-subtle, rgba(0,0,0,0.08))',
                      }}
                    />
                    <div
                      style={{
                        height: 10,
                        width: '60%',
                        borderRadius: 4,
                        background: 'var(--color-border-subtle, rgba(0,0,0,0.08))',
                      }}
                    />
                  </div>
                </div>
              ))
            : items.map((item) => (
                <ResourceRow
                  key={item.id}
                  item={item}
                  tenantHref={tenantHref}
                  fallbackLogo={fallbackLogo}
                  locale={locale}
                />
              ))}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html:
            '@keyframes ak-footer-resource-pulse{0%,100%{opacity:1}50%{opacity:.45}}' +
            '.ak-footer-resource-rail::-webkit-scrollbar{display:none}',
        }}
      />
    </div>
  );
}

/** {@code "09 May 2023"} / {@code "09 Mayıs 2025"} — day + full month
 *  name + year, no time-of-day. Falls back to the raw string when it
 *  isn't a parseable date (authored non-ISO values). */
function formatResourceDate(value: string, locale: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  try {
    return d.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    // Unknown/malformed BCP-47 tag (e.g. a locale code the runtime
    // doesn't recognise) — fall back to the browser/runtime default
    // rather than throwing and blanking the whole footer.
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'long', year: 'numeric' });
  }
}

function ResourceRow({
  item,
  tenantHref,
  fallbackLogo,
  locale,
}: {
  item: ResourceItem;
  tenantHref: (href: string | undefined) => string;
  fallbackLogo?: string;
  locale: string;
}) {
  return (
    <a
      href={tenantHref(item.href)}
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        scrollSnapAlign: 'start',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <div
        aria-hidden
        style={{
          width: 56,
          height: 56,
          flexShrink: 0,
          borderRadius: 'var(--radius-md, 10px)',
          overflow: 'hidden',
          background: item.cover
            ? `center / cover no-repeat url(${item.cover})`
            : 'var(--color-border-subtle, rgba(0,0,0,0.08))',
          display: item.cover ? undefined : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!item.cover && fallbackLogo && (
          <img
            src={fallbackLogo}
            alt=""
            style={{ width: '90%', height: '90%', objectFit: 'contain', opacity: 0.85 }}
          />
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.35,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.title}
        </div>
        {item.date && (
          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              color: 'color-mix(in srgb, currentColor 60%, transparent)',
            }}
          >
            {formatResourceDate(item.date, locale)}
          </div>
        )}
      </div>
    </a>
  );
}

function RailArrowButton({
  dir,
  onClick,
  disabled,
}: {
  dir: 'prev' | 'next';
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={dir === 'prev' ? 'Previous' : 'Next'}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={onClick}
      style={{
        width: 28,
        height: 28,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: '1px solid var(--color-border-default, rgba(0,0,0,0.15))',
        background: 'transparent',
        color: 'inherit',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.35 : 1,
        pointerEvents: disabled ? 'none' : undefined,
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        style={{ transform: dir === 'prev' ? 'scaleX(1)' : 'scaleX(-1)' }}
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
  );
}
